import { getSiteConfig, SiteKey } from './site';
import { getBasePath } from './base-path';
import {
    BaseSiteRouterManager,
    formatUrlSuffix,
    get,
    getSiteRouterConfig,
    isValidAbsoluteUrl,
    QueryKey,
    SiteRouterKey
} from '@libs/shared';
import { TEMPLATE_FILE_NAME } from '~/interfaces';
import { getCurrentLocaleKey } from '~/utils';

class SiteRouterManager extends BaseSiteRouterManager<SiteKey> {
    protected get basePath() {
        return getBasePath();
    }

    protected get site() {
        return getSiteConfig();
    }

    protected get router() {
        return getSiteRouterConfig();
    }

    /**
     * 获取相对路径（二级路径+路由）
     * @param routerKey
     * @param args
     */
    public getRelativePath(routerKey: SiteRouterKey, ...args: (string | number)[]) {
        return this.relativePath(routerKey, ...args);
    }

    /**
     * 获取带参相对路径（二级路径+路由+参数）
     * @param query
     * @param routerKey
     * @param args
     */
    public getQueryRelativePath(
        query: { key: QueryKey; value: string | number }[],
        routerKey: SiteRouterKey,
        ...args: (string | number)[]
    ) {
        return this.queryRelativePath(query, routerKey, ...args);
    }

    /**
     * 获取站点绝对路径（站点+二级路径+路由）
     * @param siteKey
     * @param routerKey
     * @param args
     */
    public getAbsolutePath(
        siteKey: SiteKey,
        routerKey: SiteRouterKey,
        ...args: (string | number)[]
    ) {
        return this.absolutePath(siteKey, routerKey, ...args);
    }

    /**
     * 获取带参站点绝对路径（站点+二级路径+路由+参数）
     * @param query
     * @param siteKey
     * @param routerKey
     * @param args
     */
    public getQueryAbsolutePath(
        query: { key: QueryKey; value: string | number }[],
        siteKey: SiteKey,
        routerKey: SiteRouterKey,
        ...args: (string | number)[]
    ) {
        return this.queryAbsolutePath(query, siteKey, routerKey, ...args);
    }

    // ……其他公共方法

    /**
     * 获取云币购买链接
     */
    public getPurchaseCoinLink() {
        const purchaseCoinLink = get(window.jdy_config, 'link.purchase_coin', '');
        if (isValidAbsoluteUrl(purchaseCoinLink)) {
            // 后台返回绝对路径时直接返回
            return purchaseCoinLink;
        }
        // 后台返回相对路径时拼接站点和二级路径后再返回
        return this.getHostPage(purchaseCoinLink);
    }

    /**
     * 获取模板文件链接
     * @param fileName
     * @param templateKey
     * @param args
     */
    public getTemplateFileSource(
        fileName: TEMPLATE_FILE_NAME,
        templateKey: SiteRouterKey = SiteRouterKey.TemplateFile,
        ...args: (string | number)[]
    ) {
        return this.getQueryAbsolutePath(
            [
                {
                    key: 'template_file_name',
                    value: fileName
                },
                {
                    key: 'locale',
                    value: getCurrentLocaleKey()
                }
            ],
            SiteKey.Site,
            templateKey,
            ...args
        );
    }

    /**
     * 获取官网各页面
     * @param key
     */
    public getHomePage(key?: keyof typeof window.jdy_config.home_site_links) {
        const homeSite = get(window.jdy_config, 'home_site', '');
        const homePage = this.addBasePath(homeSite);
        if (!key) {
            return homePage;
        }
        const subPage = get(window.jdy_config, ['home_site_links', key]);
        return subPage || homePage;
    }

    /**
     * 获取主站点地址
     * @param suffixUrl
     */
    public getHostPage(suffixUrl: string) {
        return formatUrlSuffix(this.getHostSiteWithBasePath(), suffixUrl);
    }

    /**
     * 获取API站点地址
     * @param suffixUrl
     */
    public getApiPage(suffixUrl: string) {
        return formatUrlSuffix(this.getApiSiteWithBasePath(), suffixUrl);
    }

    /**
     * 获取主站点带二级路径
     */
    public getHostSiteWithBasePath() {
        return this.getSiteWithBasePath(SiteKey.Site);
    }

    /**
     * 获取API站点带二级路径
     */
    public getApiSiteWithBasePath() {
        return this.getSiteWithBasePath(SiteKey.Api);
    }

    /**
     * 获取平台域名（fx-code/fx-corp 都会注入）
     * todo skye：临时逻辑，实现 jdy 访问管理后台走平台域名，其他请求依然走 jdy 域；jdy 对接平台 AT/RT 后可移除；
     */
    public getPlatformSiteWithBasePath() {
        const platformSite = window.jdy_config?.portal_site;
        return platformSite ? this.addBasePath(platformSite) : '';
    }
}

const siteRouterManager = new SiteRouterManager();

export default siteRouterManager;
